import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramApiSlug } from 'constants/academy'
import { checkEmailAvailability } from 'redux/thunks/check-email-availability'
import { getPaymentMethods, PaymentMethods } from 'redux/thunks/get-payment-methods'
import { PatientAttributes } from 'redux/types'
import railsApiService from 'services/api/rails-api-service'
import storefrontApiService, {
  StorefrontCheckoutParams,
  StorefrontCommonAttributes
} from 'services/api/storefront-api-service'
import { normalizeJsonApi } from 'utils/normalizeJsonApi'

export interface AcademyOnboardingState {
  paymentMethods: PaymentMethods[]
  patientAttributes: PatientAttributes | null
  variantId: string | null
  installments: number
  isLoading: boolean
  isEmailAvailable: boolean
  orderData: AcademyCheckoutResponse | null
  orderNumber: string | null
  orderToken: string | null
  totalPrice: number
  hasAppliedCoupon: boolean
  orderUserId: string | null
  enrollmentId: string | null
}

const initialState: AcademyOnboardingState = {
  paymentMethods: [],
  patientAttributes: null,
  variantId: null,
  installments: 1,
  isLoading: false,
  isEmailAvailable: false,
  orderData: null,
  orderNumber: null,
  orderToken: null,
  totalPrice: 0,
  hasAppliedCoupon: false,
  orderUserId: null,
  enrollmentId: null
}

export const academyOnboardingSlice = createSlice({
  name: 'academy-onboarding',
  initialState,
  reducers: {
    resetAcademyState: (state) => {
      state = initialState
      return state
    },
    setPatientAttributes: (state, { payload }: PayloadAction<PatientAttributes | null>) => {
      state.patientAttributes = payload
    },
    setInstallments: (state, { payload }: PayloadAction<number>) => {
      state.installments = payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPaymentMethods.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPaymentMethods.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentMethods = payload
      })
      .addCase(getPaymentMethods.rejected, (state) => {
        state.isLoading = false
        state.paymentMethods = []
      })
      .addCase(checkEmailAvailability.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkEmailAvailability.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.isEmailAvailable = payload
      })
      .addCase(checkEmailAvailability.rejected, (state) => {
        state.isLoading = false
        state.isEmailAvailable = false
      })
      .addCase(createEnrollment.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createEnrollment.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.orderData = payload

        const cartAttributes = Object.values(payload?.cart)?.[0]?.attributes

        if (cartAttributes) {
          state.orderToken = cartAttributes.token || null
          state.orderNumber = cartAttributes.number || null
          state.totalPrice = parseFloat(cartAttributes.total)
        }
        const enrollmentID = payload?.enrollment ? Object.values(payload.enrollment)[0]?.id : null
        if (enrollmentID) state.enrollmentId = enrollmentID

        const variantData = Object.values(payload?.cart)?.[0]?.relationships?.variants?.data
        if (Array.isArray(variantData)) state.variantId = variantData[0]?.id
      })
      .addCase(createEnrollment.rejected, (state) => {
        state.isLoading = false
        state.orderData = null
        state.orderToken = null
      })
      .addCase(updateAcademyVariant.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateAcademyVariant.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.orderData = {
          ...(state.orderData || {}),
          ...payload
        }
      })
      .addCase(updateAcademyVariant.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(academyCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(academyCheckout.fulfilled, (state, { payload }) => {
        state.isLoading = false

        state.orderData = {
          ...(state.orderData || {}),
          ...payload
        }

        const orderUserData = Object.values(payload.cart)?.[0]?.relationships?.user?.data
        const orderUserId = Array.isArray(orderUserData) ? orderUserData[0]?.id : orderUserData?.id

        if (orderUserId) state.orderUserId = orderUserId
      })
      .addCase(academyCheckout.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(academyApplyCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(academyApplyCoupon.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.hasAppliedCoupon = true
        state.orderData = {
          ...(state.orderData || {}),
          ...payload
        }

        const cartItem = Object.values(payload.cart)?.[0] as AcademyCartData
        const newTotalPrice = cartItem.attributes.total
        state.totalPrice = Number(newTotalPrice)
      })
      .addCase(academyApplyCoupon.rejected, (state) => {
        state.isLoading = false
        state.hasAppliedCoupon = false
        state.orderData = null
      })
})

export const { resetAcademyState, setPatientAttributes, setInstallments } = academyOnboardingSlice.actions

export default academyOnboardingSlice.reducer

interface CreateEnrollmentParams {
  program_slug: ProgramApiSlug
  enrollment?: {
    patient_attributes: {
      first_name?: string
      last_name?: string
      ssn?: string
      phone_number?: string
      terms_of_service?: boolean
      cgu_accepted?: boolean
      email?: string
      cpf_number?: string
    }
  }
  is_smart_fit?: boolean
}

export const createEnrollment = createAsyncThunk(
  'create-enrollment',
  async (
    {
      program_slug,
      enrollment = {
        patient_attributes: {}
      },
      is_smart_fit
    }: CreateEnrollmentParams,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await railsApiService.apiInstance.post(`/api/v2/academy/enrollments`, {
        program_slug,
        enrollment,
        ...(is_smart_fit && { is_smart_fit })
      })
      console.log(data)
      return (await normalizeJsonApi(data)) as AcademyCheckoutResponse
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface UpdateAcademyVariantParams {
  variantId: number
  orderToken: string
  enrollmentId: string
}

export const updateAcademyVariant = createAsyncThunk(
  'update-academy-onboarding-variant',
  async ({ variantId, orderToken, enrollmentId }: UpdateAcademyVariantParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.post(
        `/api/v2/academy/enrollments/${enrollmentId}/empty_and_add_to_cart`,
        {
          variant_id: variantId,
          orderToken
        }
      )
      return await normalizeJsonApi(data)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const academyCheckout = createAsyncThunk<AcademyCheckoutResponse, StorefrontCheckoutParams>(
  'academy-onboarding-checkout',
  async (params, { rejectWithValue }) => {
    try {
      return await storefrontApiService.checkout(params)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export interface AcademyApplyCouponParams {
  coupon: string
  orderToken: string
}

export const academyApplyCoupon = createAsyncThunk(
  'academy-onboarding-apply-coupon',
  async (params: AcademyApplyCouponParams, { rejectWithValue }) => {
    try {
      return await storefrontApiService.applyCoupon(params)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export interface AcademyCheckoutResponse {
  cart: AcademyCart
  enrollment?: AcademyEnrollment
  lineItem?: AcademyLineItems
}

export type AcademyCart = Record<string, AcademyCartData>

export interface AcademyCartData {
  id: string
  type: string
  attributes: AcademyAttributes
  relationships: AcademyCartRelationships
}

export interface AcademyAttributes {
  number: string
  itemTotal: string
  total: string
  shipTotal: string
  adjustmentTotal: string
  createdAt: string
  updatedAt: string
  completedAt: string
  includedTaxTotal: string
  additionalTaxTotal: string
  displayAdditionalTaxTotal: string
  displayIncludedTaxTotal: string
  taxTotal: string
  currency: string
  state: string
  token: string
  email: string
  displayItemTotal: string
  displayShipTotal: string
  displayAdjustmentTotal: string
  displayTaxTotal: string
  promoTotal: string
  displayPromoTotal: string
  itemCount: number
  specialInstructions: null
  displayTotal: string
  lineItemsNames: string
  gatewaySecureModeRedirectUrl: null
  pharmacistId: number
  orderType: string
  trackingLink: string
}

export interface AcademyCartRelationships {
  lineItems: StorefrontCommonAttributes
  variants: StorefrontCommonAttributes
  promotions: StorefrontCommonAttributes
  payments: StorefrontCommonAttributes
  shipments: StorefrontCommonAttributes
  user: StorefrontCommonAttributes
  billingAddress: StorefrontCommonAttributes
  shippingAddress: StorefrontCommonAttributes
}

export type AcademyLineItems = Record<string, AcademyLineItem>

export interface AcademyLineItem {
  id: string
  type: string
  attributes: AcademyLineItemAttributes
  relationships: AcademyLineItemRelationships
}

export interface AcademyLineItemAttributes {
  name: string
  quantity: number
  price: string
  slug: string
  optionsText: string
  currency: string
  displayPrice: string
  total: string
  displayTotal: string
  adjustmentTotal: string
  displayAdjustmentTotal: string
  additionalTaxTotal: string
  discountedAmount: string
  displayDiscountedAmount: string
  displayAdditionalTaxTotal: string
  promoTotal: string
  displayPromoTotal: string
  includedTaxTotal: string
  displayIncludedTaxTotal: string
}

export interface AcademyLineItemRelationships {
  variant: StorefrontCommonAttributes
}

export type AcademyEnrollment = Record<string, AcademyEnrollmentData>

export interface AcademyEnrollmentData {
  id: string
  type: string
  attributes: AcademyEnrollmentAttributes
  relationships: AcademyEnrollmentRelationships
}

export interface AcademyEnrollmentAttributes {
  state: string
  isInterruptable: boolean
}

export interface AcademyEnrollmentRelationships {
  program: StorefrontCommonAttributes
  spreeOrder: StorefrontCommonAttributes
  enrollmentPayments: StorefrontCommonAttributes
  attendances: StorefrontCommonAttributes
}
